
/**
 * ModuleConfigDrawer — the Clay-style floating configuration panel.
 *
 * Slides in from the right edge when a target column is selected. Two modes:
 *   - BROWSE: a searchable, category-grouped LIST of modules (scales to 100s).
 *   - CONFIGURE: the chosen module's config form + a focused per-cell preview,
 *     reusing <MapperInspector embedded> so the editor/preview behaviour is
 *     identical to the legacy inspector. A "Change module" affordance flips
 *     back to BROWSE.
 *
 * Save uses the page's existing save flow (edits auto-apply live; the footer
 * saves the workbook, or the template). The host re-mounts this with
 * key={column} so mode resets correctly when the user picks a different column.
 *
 * Layout: a full-height side panel, or — via the ⤢ header button — a large
 * modal with a Source / Workbook field list beside the editor, for writing
 * long prompts and inserting fields comfortably.
 */

import { useEffect, useMemo, useState } from "react";
import { Maximize2, Minimize2 } from "lucide-react";
import {
  cellModule,
  pickableCellModules,
  pickById,
  type CellModule,
} from "@/lib/mapper/modules";
import { ModuleIconView } from "@/components/icons/ModuleIconView";
import { getModuleUsage } from "@/lib/api/endpoints";
import { InlineArrow } from "@/components/icons/InlineArrow";
import { MapperInspector } from "@/pages/workbooks/sheet/MapperInspector";
import { TaxonomyEditor } from "@/pages/workbooks/sheet/TaxonomyPanel";
import type { FieldInputHandle } from "@/modules/_shared/FieldInput.client";
import { ImagesEditor } from "./ImagesEditor.client";
import { EditorExpandedContext } from "@/modules/_shared/editorLayout";
import {
  SourceColumnList,
  exportColumnCommand,
  sourceColumnCommand,
} from "@/pages/workbooks/sheet/SourcePanel";
import type {
  ImageConfig,
  LookupDictionary,
  Profile,
  ProductType,
  Rule,
  ShopifyRow,
  SourceRow,
  TaxonomyConfig,
} from "@/lib/mapper/types";

const CATEGORY_ORDER = [
  "Basics",
  "Rules",
  "AI",
  "Images",
  "Category",
  "Extra Rows",
] as const;

type Props = {
  column: string;
  rule: Rule | undefined;
  productType: ProductType;
  sourceHeaders: string[];
  sourceRows: SourceRow[];
  previewRows: ShopifyRow[];
  previewLoading: boolean;
  profile: Profile;
  onChangeRule: (r: Rule) => void;
  onClearRule: () => void;
  kindMemory: Record<string, Rule>;
  onRememberKind: (key: string, rule: Rule) => void;
  registerFocusedAutocomplete: (
    handle: FieldInputHandle | null,
  ) => void;
  onSelectColumn?: (column: string) => void;
  onClose: () => void;
  /** Save the workbook (rules + edits + AI output stay with this sheet). */
  onSave: () => void;
  /** Save the rules into the template instead. */
  onSaveTemplate?: () => void;
  saving: boolean;
  dirty: boolean;
  rightOffset?: number;
  /** Profile-level taxonomy config (powers the Shopify Product Taxonomy module). */
  taxonomy?: TaxonomyConfig;
  onChangeTaxonomy: (next: TaxonomyConfig) => void;
  /** Slug-keyed dictionary map for inline Dictionary module preview. */
  dictionaries?: Record<string, LookupDictionary>;
  /** Called after a dictionary entry is saved inline, so the page can refresh its dictionaryMap. */
  onDictionariesChanged?: () => void;
  /** Profile-level image config — the Images module owns the three image columns. */
  onChangeImages?: (next: ImageConfig) => void;
  /** Workbook (output) columns listed beside the editor in the large view. */
  exportColumns?: string[];
  /** Insert a field token into the focused text box. */
  onInsertField?: (token: string) => void;
};

const EXPANDED_STORAGE_KEY = "proly.moduleDrawer.expanded";

function readExpandedPreference(): boolean {
  try {
    return window.localStorage.getItem(EXPANDED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

/** Shopify columns written by the Images module (one row per image). */
export const IMAGE_COLUMNS: readonly string[] = ["Image Src", "Image Position", "Image Alt Text"];

export function ModuleConfigDrawer({
  column,
  rule,
  productType,
  sourceHeaders,
  sourceRows,
  previewRows,
  previewLoading,
  profile,
  onChangeRule,
  onClearRule,
  kindMemory,
  onRememberKind,
  registerFocusedAutocomplete,
  onSelectColumn,
  onClose,
  onSave,
  onSaveTemplate,
  saving,
  dirty,
  rightOffset = 0,
  taxonomy,
  onChangeTaxonomy,
  dictionaries,
  onDictionariesChanged,
  onChangeImages,
  exportColumns = [],
  onInsertField,
}: Props) {
  // Large-modal mode; remembered so the next column opens the same way.
  const [expanded, setExpanded] = useState(readExpandedPreference);
  function toggleExpanded() {
    setExpanded((current) => {
      const next = !current;
      try {
        window.localStorage.setItem(EXPANDED_STORAGE_KEY, next ? "1" : "0");
      } catch {
        /* storage disabled — still toggle for this session */
      }
      return next;
    });
  }

  // Esc leaves the large view first, then closes.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      if (expanded) {
        setExpanded(false);
        try {
          window.localStorage.setItem(EXPANDED_STORAGE_KEY, "0");
        } catch {
          /* ignore */
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [expanded]);

  const isImageColumn = !!onChangeImages && IMAGE_COLUMNS.includes(column);
  const [mode, setMode] = useState<
    "browse" | "configure" | "shopify-taxonomy" | "google-taxonomy" | "images"
  >(isImageColumn ? "images" : rule ? "configure" : "browse");
  const [query, setQuery] = useState("");
  const [usage, setUsage] = useState<Record<string, number>>({});

  // App-wide module usage → drives "most used first" ordering + a count badge.
  useEffect(() => {
    let live = true;
    getModuleUsage()
      .then((d) => {
        if (live) setUsage(d.usage ?? {});
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  // AI rules show the preset they came from ("Tag & classify with Jev", …).
  const currentModule =
    rule?.kind === "ai"
      ? (pickableCellModules().find((m) => matchesActivePreset(rule, m)) ?? cellModule("ai"))
      : rule
        ? cellModule(rule.kind)
        : undefined;

  function pickModuleById(id: string) {
    const mod = pickById(id);
    if (!mod) return;
    const remembered = kindMemory[`${column}::${id}`];
    const next = remembered ?? mod.defaultConfig();
    if (!next) return;
    onChangeRule(next);
    onRememberKind(`${column}::${id}`, next);
    setMode("configure");
  }

  function matchesActivePreset(activeRule: Rule | undefined, mod: CellModule): boolean {
    if (!activeRule) return false;
    if (activeRule.kind === "ai" && mod.kind === "ai") {
      // Each AI rule highlights exactly one preset: Jev decisions, then AI
      // handles, then the provider's "Write with …" entry.
      const preset = activeRule.decision
        ? "ai:jev"
        : activeRule.format === "slug"
          ? "ai:handle"
          : activeRule.provider === "openai"
            ? "ai:openai"
            : activeRule.provider === "openrouter"
              ? "ai:openrouter"
              : "ai:anthropic";
      return mod.id === preset;
    }
    return activeRule.kind === mod.kind;
  }

  // Grouped + searched module list. Within a category: most-used first, then
  // catalog order (stable). Empty categories are dropped.
  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = (m: CellModule) =>
      q.length === 0 ||
      m.name.toLowerCase().includes(q) ||
      m.tooltip.toLowerCase().includes(q) ||
      m.category.toLowerCase().includes(q) ||
      m.tags.some((t) => t.toLowerCase().includes(q));

    const byCat = new Map<string, { mod: CellModule; order: number }[]>();
    pickableCellModules().forEach((mod, order) => {
      if (!matches(mod)) return;
      const arr = byCat.get(mod.category) ?? [];
      arr.push({ mod, order });
      byCat.set(mod.category, arr);
    });

    const order = (cat: string) => {
      const i = CATEGORY_ORDER.indexOf(cat as (typeof CATEGORY_ORDER)[number]);
      return i === -1 ? CATEGORY_ORDER.length : i;
    };

    return Array.from(byCat.entries())
      .sort(([a], [b]) => order(a) - order(b))
      .map(([category, items]) => ({
        category,
        items: items
          .sort(
            (a, b) =>
              (usage[b.mod.kind] ?? 0) - (usage[a.mod.kind] ?? 0) ||
              a.order - b.order,
          )
          .map((x) => x.mod),
      }));
  }, [query, usage]);

  const panel = (
    <aside
      className={
        expanded
          ? "relative flex h-[94vh] w-[min(1280px,96vw)] flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl dark:border-border dark:bg-surface"
          : "fixed inset-y-0 z-40 flex w-[480px] max-w-[92vw] flex-col border-l border-slate-200 dark:border-border bg-white dark:bg-surface shadow-2xl"
      }
      style={expanded ? undefined : { right: rightOffset }}
      role="dialog"
      aria-modal={expanded || undefined}
      aria-label={`Configure ${column}`}
    >
      {/* Header — in configure mode: back + module name (title) over the column
          name; otherwise the target-column identity for browsing. */}
      <div className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 dark:border-border px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          {mode === "configure" && currentModule ? (
            <>
              <button
                type="button"
                onClick={() => setMode("browse")}
                title="Back to modules"
                aria-label="Back to modules"
                className="shrink-0 rounded border border-slate-200 dark:border-border p-1 text-slate-500 dark:text-slate-400 transition hover:bg-slate-100 dark:hover:bg-surface-2 hover:text-slate-900 dark:hover:text-slate-100"
              >
                <InlineArrow direction="left" size={14} />
              </button>
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-slate-100 dark:bg-surface-2 text-slate-600 dark:text-slate-300 ring-1 ring-slate-200 dark:ring-slate-700">
                <ModuleIconView icon={currentModule.icon} size={14} />
              </span>
              <div className="min-w-0">
                <div
                  className="truncate text-[14px] font-semibold leading-tight text-slate-900 dark:text-slate-100"
                  title={currentModule.name}
                >
                  {currentModule?.name ?? rule?.kind ?? "Module"}
                </div>
                <div className="mt-0.5 flex items-center gap-1 leading-tight">
                  <span className="shrink-0 text-[9px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                    Column
                  </span>
                  <span
                    className="truncate font-mono text-[11px] text-slate-600 dark:text-slate-300"
                    title={column}
                  >
                    {column}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <>
              <StatusDot rule={rule} />
              <div className="min-w-0">
                <div className="text-[9.5px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                  Target column
                </div>
                <div
                  className="break-words font-mono text-[14px] font-semibold leading-tight text-slate-900 dark:text-slate-100"
                  title={column}
                >
                  {column}
                </div>
              </div>
            </>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={toggleExpanded}
            title={expanded ? "Back to side panel (Esc)" : "Open large editor"}
            aria-label={expanded ? "Back to side panel" : "Open large editor"}
            aria-pressed={expanded}
            className="rounded border border-slate-200 dark:border-border p-1 text-slate-500 dark:text-slate-400 transition hover:bg-slate-100 dark:hover:bg-surface-2 hover:text-slate-900 dark:hover:text-slate-100"
          >
            {expanded ? (
              <Minimize2 className="h-3.5 w-3.5" />
            ) : (
              <Maximize2 className="h-3.5 w-3.5" />
            )}
          </button>
          {rule && (
            <button
              type="button"
              onClick={onClearRule}
              title="Clear this module"
              aria-label="Clear this module"
              className="rounded border border-slate-200 dark:border-border px-1.5 py-1 text-[12px] leading-none text-slate-500 dark:text-slate-400 transition hover:border-red-300 dark:hover:border-red-700 hover:bg-red-50 dark:hover:bg-red-950/40 hover:text-red-700 dark:hover:text-red-300"
            >
              ↩
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            title="Close"
            aria-label="Close"
            className="rounded border border-slate-200 dark:border-border px-1.5 py-1 text-[13px] leading-none text-slate-500 dark:text-slate-400 transition hover:bg-slate-100 dark:hover:bg-surface-2 hover:text-slate-900 dark:hover:text-slate-100"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex min-h-0 flex-1">
      <div className="flex min-w-0 min-h-0 flex-1 flex-col">
      {mode === "images" && onChangeImages ? (
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          <div className="mb-3 flex items-center gap-2">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-slate-100 dark:bg-surface-2 text-slate-600 dark:text-slate-300 ring-1 ring-slate-200 dark:ring-slate-700">
              <ModuleIconView icon={{ kind: "lucide", name: "Images" }} size={13} />
            </span>
            <span className="text-[12.5px] font-semibold text-slate-800 dark:text-slate-200">
              Images module
            </span>
            <span className="text-[11px] text-slate-500 dark:text-slate-400">
              fills Image Src, Image Position and Image Alt Text
            </span>
          </div>
          <ImagesEditor
            images={profile.images}
            onChange={onChangeImages}
            sourceHeaders={sourceHeaders}
            outputColumns={Object.keys(profile.fields)}
            sampleRow={sourceRows[0]}
            sampleOutput={previewRows[0]}
            registerFocusedAutocomplete={registerFocusedAutocomplete}
          />
        </div>
      ) : mode === "browse" ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="shrink-0 px-4 pt-3">
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search modules…"
              className="w-full rounded-md border border-slate-300 dark:border-border bg-white dark:bg-surface px-3 py-1.5 text-[12.5px] focus:border-slate-500 dark:focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300 dark:focus:ring-slate-600"
            />
            <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
              Pick how this column gets filled.
            </p>
          </div>

          <div className="mt-2 min-h-0 flex-1 overflow-y-auto px-3 pb-4">
            {/* Taxonomy modules — profile-level, so pinned above the cell modules. */}
            {(() => {
              const q = query.trim().toLowerCase();
              const matches = (kw: string) => q === "" || kw.includes(q) || q.includes(kw.split(" ")[0]);
              const showShopify = matches("shopify product category taxonomy attribute");
              const showGoogle = matches("google product category taxonomy shopping");
              if (!showShopify && !showGoogle) return null;
              return (
                <div className="mb-3">
                  <div className="px-1 pb-1 pt-2 text-[9.5px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                    Category
                  </div>
                  {showShopify && (
                    <TaxonomyCard
                      brand="shopify"
                      title="Shopify Product Category"
                      subtitle="Pick a category → add its attributes as columns"
                      onClick={() => setMode("shopify-taxonomy")}
                    />
                  )}
                  {showGoogle && (
                    <TaxonomyCard
                      brand="google"
                      title="Google Product Category"
                      subtitle="Pick a Google Shopping product category"
                      onClick={() => setMode("google-taxonomy")}
                    />
                  )}
                </div>
              );
            })()}
            {grouped.length === 0 && (
              <p className="px-1 py-6 text-center text-[12px] text-slate-400 dark:text-slate-500">
                No modules match “{query}”.
              </p>
            )}
            {grouped.map((group) => (
              <div key={group.category} className="mb-3">
                <div className="px-1 pb-1 pt-2 text-[9.5px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                  {group.category}
                </div>
                <div className="space-y-0.5">
                  {group.items.map((mod) => {
                    const modId = mod.id ?? mod.kind;
                    const count = usage[mod.kind] ?? 0;
                    const active = matchesActivePreset(rule, mod);
                    return (
                      <button
                        key={modId}
                        type="button"
                        onClick={() => pickModuleById(modId)}
                        title={mod.tooltip}
                        className={[
                          "group flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition",
                          active
                            ? "bg-slate-900 dark:bg-slate-100 dark:text-slate-950 text-white"
                            : "hover:bg-slate-100 dark:hover:bg-surface-2",
                        ].join(" ")}
                      >
                        <span
                          className={[
                            "flex h-7 w-7 shrink-0 items-center justify-center rounded-md ring-1",
                            active
                              ? "bg-white/10 ring-white/20"
                              : "bg-slate-50 dark:bg-background text-slate-600 dark:text-slate-400 ring-slate-200 dark:ring-slate-700",
                          ].join(" ")}
                        >
                          <ModuleIconView icon={mod.icon} size={15} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5">
                            <span className="truncate text-[12.5px] font-medium">
                              {mod.name}
                            </span>
                            {count > 0 && (
                              <span
                                title={`Used ${count} time(s) across all templates`}
                                className={[
                                  "shrink-0 rounded px-1 py-px text-[9px] font-semibold leading-none ring-1",
                                  active
                                    ? "bg-white/15 text-white ring-white/20"
                                    : "bg-slate-100 dark:bg-surface-2 text-slate-500 dark:text-slate-400 ring-slate-200 dark:ring-slate-700",
                                ].join(" ")}
                              >
                                {count}
                              </span>
                            )}
                          </span>
                          <span
                            className={[
                              "mt-0.5 block truncate text-[11px]",
                              active ? "text-white/70" : "text-slate-500 dark:text-slate-400",
                            ].join(" ")}
                          >
                            {mod.tooltip}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : mode === "shopify-taxonomy" || mode === "google-taxonomy" ? (
        <div className="flex min-h-0 flex-1 flex-col">
          {/* Taxonomy module bar + back affordance */}
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-100 dark:border-border bg-slate-50/70 px-4 py-2">
            <div className="flex min-w-0 items-center gap-2">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-white dark:bg-surface text-slate-600 dark:text-slate-400 ring-1 ring-slate-200 dark:ring-slate-700">
                <ModuleIconView
                  icon={{ kind: "brand", name: mode === "shopify-taxonomy" ? "shopify" : "google" }}
                  size={13}
                />
              </span>
              <span className="truncate text-[12.5px] font-semibold text-slate-800 dark:text-slate-200">
                {mode === "shopify-taxonomy" ? "Shopify Product Category" : "Google Product Category"}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setMode(rule ? "configure" : "browse")}
              className="shrink-0 rounded border border-slate-300 dark:border-border bg-white dark:bg-surface px-2 py-0.5 text-[11px] font-medium text-slate-700 dark:text-slate-300 transition hover:bg-slate-100 dark:hover:bg-surface-2"
            >
              Back
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
            <p className="mb-3 text-[11px] leading-snug text-slate-500 dark:text-slate-400">
              {mode === "shopify-taxonomy" ? (
                <>
                  Sets <span className="font-mono text-slate-700 dark:text-slate-300">Product Category</span> for this
                  sheet and adds the category&apos;s attributes as metafield columns. Tick an
                  attribute to add a column; choose a Shopify value, a custom value, or map it from
                  a sheet column.
                </>
              ) : (
                <>
                  Sets the{" "}
                  <span className="font-mono text-slate-700 dark:text-slate-300">
                    Google Shopping / Google Product Category
                  </span>{" "}
                  column for this sheet (full category path).
                </>
              )}
            </p>
            <TaxonomyEditor
              taxonomy={taxonomy}
              sourceHeaders={sourceHeaders}
              onChange={onChangeTaxonomy}
              target={mode === "shopify-taxonomy" ? "shopify" : "google"}
              inspectorContext={{
                productType,
                sourceRows,
                previewRows,
                previewLoading,
                profile,
                kindMemory,
                onRememberKind,
              }}
            />
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          {/* Reused editor + inline per-cell preview (module identity + back now
              live in the drawer header). */}
          <div className="min-h-0 flex-1 overflow-y-auto">
            <MapperInspector
              embedded
              column={column}
              rule={rule}
              productType={productType}
              sourceHeaders={sourceHeaders}
              sourceRows={sourceRows}
              previewRows={previewRows}
              previewLoading={previewLoading}
              profile={profile}
              onChangeRule={onChangeRule}
              onClearRule={onClearRule}
              kindMemory={kindMemory}
              onRememberKind={onRememberKind}
              registerFocusedAutocomplete={registerFocusedAutocomplete}
              onSelectColumn={onSelectColumn}
              dictionaries={dictionaries}
              onDictionariesChanged={onDictionariesChanged}
            />
          </div>
        </div>
      )}

      </div>
      {expanded && onInsertField && (
        <FieldsColumn
          sourceColumns={sourceHeaders}
          exportColumns={exportColumns}
          onInsert={onInsertField}
        />
      )}
      </div>

      {/* Footer — save */}
      <div className="flex shrink-0 items-center justify-between gap-2 border-t border-slate-200 dark:border-border bg-white dark:bg-surface px-4 py-2.5">
        <span className="text-[11px] text-slate-500 dark:text-slate-400">
          {dirty ? "Unsaved changes" : "All changes saved"}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-slate-300 dark:border-border bg-white dark:bg-surface px-2.5 py-1.5 text-[12px] font-medium text-slate-700 dark:text-slate-300 transition hover:bg-slate-50 dark:hover:bg-surface-2"
          >
            Done
          </button>
          {onSaveTemplate && (
            <button
              type="button"
              onClick={onSaveTemplate}
              disabled={saving}
              title="Save these rules into the template (other workbooks using it get them too)"
              className="rounded border border-slate-300 dark:border-border bg-white dark:bg-surface px-2.5 py-1.5 text-[12px] font-medium text-slate-700 dark:text-slate-300 transition hover:bg-slate-50 dark:hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Save template
            </button>
          )}
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            title="Save workbook (⌘S): keep these rules, edits and AI output with this workbook"
            className="rounded bg-slate-900 dark:bg-slate-100 dark:text-slate-950 px-3 py-1.5 text-[12px] font-medium text-white transition hover:bg-slate-700 dark:hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save workbook"}
          </button>
        </div>
      </div>
    </aside>
  );

  return (
    <EditorExpandedContext.Provider value={expanded}>
      {expanded ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-2 dark:bg-background/70">
          {panel}
        </div>
      ) : (
        panel
      )}
    </EditorExpandedContext.Provider>
  );
}

/**
 * Field list beside the editor in the large view: source file columns first,
 * then workbook columns, each under its own heading. Clicking inserts the
 * field into the text box that has focus.
 */
function FieldsColumn({
  sourceColumns,
  exportColumns,
  onInsert,
}: {
  sourceColumns: string[];
  exportColumns: string[];
  onInsert: (token: string) => void;
}) {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const filter = (columns: string[]) =>
    q ? columns.filter((column) => column.toLowerCase().includes(q)) : columns;

  return (
    <aside className="flex w-72 shrink-0 flex-col border-l border-slate-200 bg-slate-50/60 dark:border-border dark:bg-background">
      <div className="shrink-0 border-b border-slate-200 px-3 py-2 dark:border-border">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">
          Fields
        </div>
        <p className="mt-0.5 text-[10.5px] leading-snug text-slate-500 dark:text-slate-400">
          Click in a text box, then pick a field to insert it.
        </p>
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Filter fields…"
          className="mt-2 w-full rounded border border-slate-300 bg-white px-2 py-1 text-[12px] focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300 dark:border-border dark:bg-surface dark:focus:border-slate-400 dark:focus:ring-slate-600"
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
        <div className="sticky top-0 z-10 bg-slate-50 px-1 pb-1 pt-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:bg-background dark:text-slate-400">
          Source file
        </div>
        <SourceColumnList
          columns={filter(sourceColumns)}
          onInsert={onInsert}
          tokenize={sourceColumnCommand}
          display={sourceColumnCommand}
        />
        <div className="sticky top-0 z-10 mt-3 bg-slate-50 px-1 pb-1 pt-0.5 text-[10px] font-bold uppercase tracking-wider text-violet-700 dark:bg-background dark:text-violet-300">
          Workbook
        </div>
        <SourceColumnList
          columns={filter(exportColumns)}
          onInsert={onInsert}
          tokenize={exportColumnCommand}
          display={exportColumnCommand}
        />
      </div>
    </aside>
  );
}

function TaxonomyCard({
  brand,
  title,
  subtitle,
  onClick,
}: {
  brand: "shopify" | "google";
  title: string;
  subtitle: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={subtitle}
      className="group flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition hover:bg-slate-100 dark:hover:bg-surface-2"
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-50 dark:bg-background text-slate-600 dark:text-slate-400 ring-1 ring-slate-200 dark:ring-slate-700">
        <ModuleIconView icon={{ kind: "brand", name: brand }} size={15} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[12.5px] font-medium">{title}</span>
        <span className="mt-0.5 block truncate text-[11px] text-slate-500 dark:text-slate-400">{subtitle}</span>
      </span>
    </button>
  );
}

function StatusDot({ rule }: { rule: Rule | undefined }) {
  if (!rule) {
    return (
      <span
        aria-hidden
        title="No module yet"
        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-slate-300"
      />
    );
  }
  const cls = rule.kind === "ai" ? "bg-violet-500" : "bg-emerald-500";
  return (
    <span
      aria-hidden
      title={rule.kind === "ai" ? "Configured (AI)" : "Configured"}
      className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${cls}`}
    />
  );
}
